import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@tsin-env/environment';
import { Observable } from 'rxjs';

const BASE = `${environment.apiUrl2}/communications`;

/** Message direction — staff to candidate or candidate reply.
 *  Direction du message — personnel vers candidat ou réponse du candidat. */
export type MessageDirection = 'inbound' | 'outbound';

/** A single message in a candidate conversation thread.
 *  Un message dans le fil de conversation d'un candidat. */
export interface CandidateMessage {
  _id: string;
  applicationId: string;
  candidateId: string;
  direction: MessageDirection;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  sentAt: string;
  sentBy?: string;
  threadId: string;
  candidateEmail?: string;
  sesMessageId?: string;
  readAt?: string | null;
}

/** Payload to send a new outbound message.
 *  Charge utile pour envoyer un nouveau message sortant. */
export interface SendMessagePayload {
  applicationId: string;
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  /** Leave null to start a new thread / Laisser null pour démarrer un nouveau fil */
  threadId?: string;
}

/** Candidate communications HTTP service.
 *  Service HTTP pour la messagerie candidat. */
@Injectable({ providedIn: 'root' })
export class CommunicationsHttpService {
  private readonly http = inject(HttpClient);

  /** Fetch the full conversation thread for an application.
   *  Récupérer le fil de conversation complet d'une candidature. */
  getThread(applicationId: string): Observable<CandidateMessage[]> {
    return this.http.get<CandidateMessage[]>(`${BASE}/${applicationId}`);
  }

  /** Get unread inbound message count for an application.
   *  Obtenir le nombre de messages entrants non lus pour une candidature. */
  getUnreadCount(applicationId: string): Observable<{ unreadCount: number }> {
    return this.http.get<{ unreadCount: number }>(`${BASE}/${applicationId}/unread-count`);
  }

  /** Send a new outbound message to a candidate.
   *  Envoyer un nouveau message sortant à un candidat. */
  sendMessage(payload: SendMessagePayload): Observable<CandidateMessage> {
    return this.http.post<CandidateMessage>(`${BASE}/send`, payload);
  }

  /** Mark a list of messages as read.
   *  Marquer une liste de messages comme lus. */
  markRead(messageIds: string[]): Observable<void> {
    return this.http.patch<void>(`${BASE}/mark-read`, { messageIds });
  }
}
